import { useMemo } from 'react';
import { Box, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material';
import { tokens } from '../theme';
import { useTranslation } from 'react-i18next';
import { convertSpeed, getUnits } from '../pages/WorkoutAnalysisV2';

const GradientAnalysisSection = ({ chunks }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // < 900px
  const units = getUnits();
  const speedLabel = units === 'imperial' ? 'mph' : 'km/h';

  const formatTimeDiff = (seconds) => {
    const absSeconds = Math.abs(seconds);
    const minutes = Math.floor(absSeconds / 60);
    const secs = Math.round(absSeconds % 60);
    const sign = seconds < 0 ? '-' : '+';
    return `${sign}${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const gradientCategories = useMemo(() => {
    const categories = {
      'Steep Descent (< -5%)': [],
      'Descent (-5% to -2%)': [],
      'Slight Descent (-2% to 0%)': [],
      'Flat (0% to 2%)': [],
      'Slight Climb (2% to 5%)': [],
      'Climb (5% to 8%)': [],
      'Steep Climb (> 8%)': []
    };

    chunks.forEach(chunk => {
      const gradient = chunk.avg_gradient;
      if (gradient < -5) categories['Steep Descent (< -5%)'].push(chunk);
      else if (gradient < -2) categories['Descent (-5% to -2%)'].push(chunk);
      else if (gradient < 0) categories['Slight Descent (-2% to 0%)'].push(chunk);
      else if (gradient < 2) categories['Flat (0% to 2%)'].push(chunk);
      else if (gradient < 5) categories['Slight Climb (2% to 5%)'].push(chunk);
      else if (gradient < 8) categories['Climb (5% to 8%)'].push(chunk);
      else categories['Steep Climb (> 8%)'].push(chunk);
    });

    return categories;
  }, [chunks]);

  const calculateCategorySummary = (categoryChunks) => {
    if (categoryChunks.length === 0) return null;

    const totalTime = categoryChunks.reduce((sum, c) => sum + c.duration, 0);
    const totalDistance = categoryChunks.reduce((sum, c) => sum + c.distance, 0);
    const avgSpeed = categoryChunks.reduce((sum, c) => sum + c.avg_speed * c.duration, 0) / totalTime;
    const avgPower = categoryChunks.reduce((sum, c) => sum + c.avg_power * c.duration, 0) / totalTime;
    const avgGradient = categoryChunks.reduce((sum, c) => sum + c.avg_gradient * c.duration, 0) / totalTime;

    // Compute average curvature index for this category
    const curvatureSum = categoryChunks.reduce((sum, c) => sum + (typeof c.curvature_index === 'number' ? c.curvature_index : 0), 0);
    const curvatureCount = categoryChunks.filter(c => typeof c.curvature_index === 'number').length;
    const avgCurvatureIndex = curvatureCount > 0 ? Math.round(curvatureSum / curvatureCount) : null;

    // Map curvature index to label and description
    const curvatureLabel = avgCurvatureIndex ? t(`twistiness${avgCurvatureIndex}`) : '';

    let timeDiffPosition = 0;
    let timeDiffWeight = 0;
    let timeDiffTotal = 0;
    let avgSpeedWithPosition = 0;
    let avgSpeedWithWeight = 0;
    let avgSpeedWithBoth = 0;
    
    const actualPositionCounts = {};
    const whatIfPositionCounts = {};

    categoryChunks.forEach(chunk => {
      if (chunk.what_if) {
        timeDiffPosition += chunk.what_if.time_diff_position;
        timeDiffWeight += chunk.what_if.time_diff_weight;
        timeDiffTotal += chunk.what_if.time_diff_total;
        avgSpeedWithPosition += chunk.what_if.speed_with_position_change * chunk.duration;
        avgSpeedWithWeight += chunk.what_if.speed_with_weight_change * chunk.duration;
        avgSpeedWithBoth += chunk.what_if.speed_with_both_changes * chunk.duration;
        
        const actualPos = chunk.what_if.actual_position;
        const whatIfPos = chunk.what_if.what_if_position;
        actualPositionCounts[actualPos] = (actualPositionCounts[actualPos] || 0) + 1;
        whatIfPositionCounts[whatIfPos] = (whatIfPositionCounts[whatIfPos] || 0) + 1;
      }
    });

    avgSpeedWithPosition = avgSpeedWithPosition / totalTime;
    avgSpeedWithWeight = avgSpeedWithWeight / totalTime;
    avgSpeedWithBoth = avgSpeedWithBoth / totalTime;
    
    const positionKeyMap = {
      'road_drops': 'roadDrops',
      'road_hoods': 'roadHoods',
      'road_aero_hoods': 'roadAeroHoods',
      'road_aero_aerobar': 'roadAeroAerobar',
      'road_aero_drops': 'roadAeroDrops',
      'gravel_hoods': 'gravelHoods',
      'gravel_drops': 'gravelDrops',
      'tt_aero': 'ttAero',
      'tt_base': 'ttBase'
    };
    
    const totalChunks = categoryChunks.filter(c => c.what_if).length;
    const actualPositionStr = Object.entries(actualPositionCounts)
      .map(([pos, count]) => `${Math.round(count / totalChunks * 100)}% ${t(positionKeyMap[pos] || pos)}`)
      .join(', ');
    const whatIfPositionStr = Object.entries(whatIfPositionCounts)
      .map(([pos, count]) => `${Math.round(count / totalChunks * 100)}% ${t(positionKeyMap[pos] || pos)}`)
      .join(', ');

    return {
      count: categoryChunks.length,
      totalTime,
      totalDistance,
      avgSpeed,
      avgPower,
      avgGradient,
      avgCurvatureIndex,
      curvatureLabel,
      timeDiffPosition,
      timeDiffWeight,
      timeDiffTotal,
      avgSpeedWithPosition,
      avgSpeedWithWeight,
      avgSpeedWithBoth,
      actualPosition: actualPositionStr,
      whatIfPosition: whatIfPositionStr
    };
  };

  const getTranslatedCategoryName = (category) => {
    switch(category) {
      case 'Steep Descent (< -5%)': return t('steepDescent');
      case 'Descent (-5% to -2%)': return t('descent');
      case 'Slight Descent (-2% to 0%)': return t('slightDescent');
      case 'Flat (0% to 2%)': return t('flat');
      case 'Slight Climb (2% to 5%)': return t('slightClimb');
      case 'Climb (5% to 8%)': return t('climb');
      case 'Steep Climb (> 8%)': return t('steepClimb');
      default: return category;
    }
  };

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" color={colors.grey[100]} mb={3}>
        {t('gradientImpactAnalysis')}
      </Typography>
      <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: 'repeat(2, 1fr)' }} gap={2}>
        {Object.entries(gradientCategories).map(([category, categoryChunks]) => {
          const summary = calculateCategorySummary(categoryChunks);
          if (!summary) return null;

          return (
            <Paper
              key={category}
              elevation={3}
              sx={{
                p: 2,
                backgroundColor: colors.grey[700],
                borderRadius: "4px"
              }}
            >
              <Typography variant="h6" color={colors.grey[100]} fontWeight="bold" mb={1}>
                {getTranslatedCategoryName(category)}
              </Typography>
              <Typography variant="body2" color={colors.grey[300]}>
                {t('timeLabel')}: {(summary.totalTime / 60).toFixed(1)} min
              </Typography>
              <Typography variant="body2" color={colors.grey[300]}>
                {t('avgSpeedLabel')}: {convertSpeed(summary.avgSpeed, units).toFixed(1)} {speedLabel}
              </Typography>
              <Typography variant="body2" color={colors.grey[300]}>
                {t('avgPowerLabel')}: {summary.avgPower.toFixed(0)} W
              </Typography>
              {summary.avgCurvatureIndex !== null && (
                <Typography variant="body2" color={colors.grey[300]}>
                  {t('twistiness')}: {summary.curvatureLabel}
                  {summary.avgCurvatureIndex !== null && summary.curvatureLabel ? ` (${summary.avgCurvatureIndex})` : ''}
                </Typography>
              )}
              
              <Box mt={2} sx={{ borderTop: `1px solid ${colors.grey[600]}`, pt: 1 }}>
                <Typography variant="caption" color={colors.grey[400]} fontWeight="bold" display="block" mb={1}>
                  {t('whatIfAnalysisLabel')}:
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ color: colors.grey[300], fontSize: '11px', fontWeight: 'bold', py: 0.5, px: 1, borderBottom: `1px solid ${colors.grey[600]}` }}>{t('scenario')}</TableCell>
                        <TableCell align="right" sx={{ color: colors.grey[300], fontSize: '11px', fontWeight: 'bold', py: 0.5, px: 1, borderBottom: `1px solid ${colors.grey[600]}` }}>{t('speed')}</TableCell>
                        <TableCell align="right" sx={{ color: colors.grey[300], fontSize: '11px', fontWeight: 'bold', py: 0.5, px: 1, borderBottom: `1px solid ${colors.grey[600]}` }}>{t('change')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell sx={{ color: colors.grey[300], fontSize: '11px', py: 0.5, px: 1, borderBottom: 'none' }}>{t('baseline')} ({summary.actualPosition})</TableCell>
                        <TableCell align="right" sx={{ color: colors.grey[300], fontSize: '11px', py: 0.5, px: 1, borderBottom: 'none' }}>{convertSpeed(summary.avgSpeed, units).toFixed(2)} {speedLabel}</TableCell>
                        <TableCell align="right" sx={{ color: colors.grey[300], fontSize: '11px', py: 0.5, px: 1, borderBottom: 'none' }}>-</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ color: colors.grey[300], fontSize: '11px', py: 0.5, px: 1, borderBottom: 'none' }}>{t('positionOnly')} ({summary.whatIfPosition})</TableCell>
                        <TableCell align="right" sx={{ color: colors.grey[300], fontSize: '11px', py: 0.5, px: 1, borderBottom: 'none' }}>{convertSpeed(summary.avgSpeedWithPosition, units).toFixed(2)} {speedLabel}</TableCell>
                        <TableCell align="right" sx={{ color: Math.abs(summary.timeDiffPosition) < 30 ? colors.blueAccent[400] : summary.avgSpeedWithPosition >= summary.avgSpeed ? colors.greenAccent[400] : colors.redAccent[400], fontSize: '11px', py: 0.5, px: 1, borderBottom: 'none' }}>
                          {summary.avgSpeedWithPosition >= summary.avgSpeed ? '+' : ''}{convertSpeed(summary.avgSpeedWithPosition - summary.avgSpeed, units).toFixed(2)} {speedLabel}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ color: colors.grey[300], fontSize: '11px', py: 0.5, px: 1, borderBottom: 'none' }}>{t('weightOnly')}</TableCell>
                        <TableCell align="right" sx={{ color: colors.grey[300], fontSize: '11px', py: 0.5, px: 1, borderBottom: 'none' }}>{convertSpeed(summary.avgSpeedWithWeight, units).toFixed(2)} {speedLabel}</TableCell>
                        <TableCell align="right" sx={{ color: Math.abs(summary.timeDiffWeight) < 30 ? colors.blueAccent[400] : summary.avgSpeedWithWeight >= summary.avgSpeed ? colors.greenAccent[400] : colors.redAccent[400], fontSize: '11px', py: 0.5, px: 1, borderBottom: 'none' }}>
                          {summary.avgSpeedWithWeight >= summary.avgSpeed ? '+' : ''}{convertSpeed(summary.avgSpeedWithWeight - summary.avgSpeed, units).toFixed(2)} {speedLabel}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ color: colors.grey[300], fontSize: '11px', py: 0.5, px: 1, borderBottom: `1px solid ${colors.grey[600]}` }}>{t('bothChanges')}</TableCell>
                        <TableCell align="right" sx={{ color: colors.grey[300], fontSize: '11px', py: 0.5, px: 1, borderBottom: `1px solid ${colors.grey[600]}` }}>{convertSpeed(summary.avgSpeedWithBoth, units).toFixed(2)} {speedLabel}</TableCell>
                        <TableCell align="right" sx={{ color: Math.abs(summary.timeDiffTotal) < 30 ? colors.blueAccent[400] : summary.avgSpeedWithBoth >= summary.avgSpeed ? colors.greenAccent[400] : colors.redAccent[400], fontSize: '11px', py: 0.5, px: 1, borderBottom: `1px solid ${colors.grey[600]}` }}>
                          {summary.avgSpeedWithBoth >= summary.avgSpeed ? '+' : ''}{convertSpeed(summary.avgSpeedWithBoth - summary.avgSpeed, units).toFixed(2)} {speedLabel}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
                <Typography 
                  variant="body2" 
                  color={Math.abs(summary.timeDiffTotal) < 30 ? colors.blueAccent[400] : summary.timeDiffTotal < 0 ? colors.greenAccent[400] : colors.redAccent[400]}
                  fontWeight="bold"
                  mt={1}
                >
                  {t('totalTimeLabel')}: {formatTimeDiff(summary.timeDiffTotal)}
                </Typography>
              </Box>
            </Paper>
          );
        })}
      </Box>
    </Box>
  );
};

export default GradientAnalysisSection;
